import { useBlotterGroupingToggling } from '@talos/kyoko';
import type { UseBlotterTableUtilitiesOutput } from '@talos/kyoko/src/components/BlotterTable/types';
import type { StateUpdatedEvent } from 'ag-grid-community';
import { POSITIONS_DERIVATIVES_GROUPINGS } from './positionsDerivativesGroupings';

interface UsePositionsDerivativesBlotterGroupingParams {
  setRowGroupColumns: UseBlotterTableUtilitiesOutput<unknown>['setRowGroupColumns'];
  wrap?: boolean;
  useMobileLabels?: boolean;
}

interface UsePositionsDerivativesBlotterGroupingOutput {
  onGroupingStateUpdated: (event: StateUpdatedEvent) => void;
  groupingToggleButtons: ReturnType<typeof useBlotterGroupingToggling>['groupingToggleButtons'];
}

/**
 * Shared row-group toggling for desktop and mobile derivatives positions blotters.
 *
 * Wire `onGroupingStateUpdated` into grid state via `useCallbackRef` before `useWsBlotterTable`:
 *
 * ```ts
 * const handleGridStateUpdated = useCallbackRef((event) => {
 *   onGroupingStateUpdated(event);
 * });
 * const blotterTable = useWsBlotterTable({ gridOptions: { onStateUpdated: handleGridStateUpdated, showOpenedGroup: true } });
 * const { onGroupingStateUpdated } = usePositionsDerivativesBlotterGrouping({ setRowGroupColumns: blotterTable.setRowGroupColumns });
 * ```
 */
export function usePositionsDerivativesBlotterGrouping({
  setRowGroupColumns,
  wrap,
  useMobileLabels,
}: UsePositionsDerivativesBlotterGroupingParams): UsePositionsDerivativesBlotterGroupingOutput {
  const { onStateUpdated: onGroupingStateUpdated, groupingToggleButtons } = useBlotterGroupingToggling({
    blotterName: 'positions derivatives',
    setRowGroupColumns,
    groupings: POSITIONS_DERIVATIVES_GROUPINGS,
    allowNoGrouping: true,
    wrap,
    useMobileLabels,
  });

  return { onGroupingStateUpdated, groupingToggleButtons };
}
